/**
 * Inline live camera barcode scanner (no modal wrapper) — embed it INSIDE a scan
 * dialog next to a manual-entry field so one window offers both camera + typed
 * input. While ``active`` it opens the rear camera, renders the <video> preview,
 * and polls it via BarcodeDetector at ~5Hz, firing ``onDetected`` for each
 * decode (with a short cooldown so the same code isn't spammed). Tears the
 * camera down when ``active`` goes false / on unmount.
 *
 * Same support envelope as CameraScanModal: BarcodeDetector + mediaDevices are
 * Chromium-only and need a secure context — gate the rendering on
 * ``cameraScanSupported`` at the call site; this self-guards too and reports via
 * ``onError``.
 */
import { useEffect, useRef, useState } from "react";
import { Typography } from "antd";

import {
  type BarcodeDetectorLike,
  cameraScanSupported,
} from "../utils/cameraScan";

const { Text } = Typography;

export function CameraScanView({
  active,
  onDetected,
  onError,
  hint = "Point the rear camera at a product barcode or voucher.",
}: {
  active: boolean;
  onDetected: (code: string) => void;
  onError?: (message: string) => void;
  hint?: string;
}) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [flash, setFlash] = useState(false);
  // Latest-ref so a parent re-render (new callbacks) never restarts the stream.
  const cbRef = useRef({ onDetected, onError });
  useEffect(() => {
    cbRef.current = { onDetected, onError };
  });

  useEffect(() => {
    if (!active) return;
    if (typeof window !== "undefined" && !window.isSecureContext) {
      cbRef.current.onError?.("Camera scanning needs HTTPS (or localhost).");
      return;
    }
    if (!cameraScanSupported) {
      cbRef.current.onError?.("This browser can't scan with the camera.");
      return;
    }

    let cancelled = false;
    let intervalId: ReturnType<typeof setInterval> | null = null;
    let detector: BarcodeDetectorLike | null = null;
    let boundVideo: HTMLVideoElement | null = null;
    let lastFiredAt = 0;

    const start = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: "environment" },
        });
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        const video = videoRef.current;
        if (video) {
          boundVideo = video;
          video.srcObject = stream;
          await video.play().catch(() => undefined);
        }
        const Ctor = window.BarcodeDetector;
        if (!Ctor) return;
        detector = new Ctor();
        intervalId = setInterval(async () => {
          if (cancelled) return;
          const v = videoRef.current;
          if (!v || v.readyState < 2 || !detector) return;
          try {
            const codes = await detector.detect(v);
            if (cancelled) return;
            const code = codes[0]?.rawValue;
            const now = Date.now();
            // Cooldown so one barcode held in frame doesn't fire repeatedly.
            if (code && now - lastFiredAt > 1500) {
              lastFiredAt = now;
              setFlash(true);
              window.setTimeout(() => setFlash(false), 250);
              cbRef.current.onDetected(code);
            }
          } catch {
            // detect() throws on intermittent frames — best-effort, ignore.
          }
        }, 200);
      } catch {
        if (cancelled) return;
        cbRef.current.onError?.("Camera unavailable — check browser permissions.");
      }
    };
    void start();

    return () => {
      cancelled = true;
      if (intervalId) clearInterval(intervalId);
      const stream = streamRef.current;
      if (stream) stream.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
      if (boundVideo) boundVideo.srcObject = null;
      setFlash(false);
    };
  }, [active]);

  return (
    <div>
      <div
        style={{
          position: "relative",
          width: "100%",
          background: "black",
          borderRadius: 8,
          overflow: "hidden",
          outline: flash ? "4px solid #52c41a" : "none",
        }}
      >
        <video
          ref={videoRef}
          playsInline
          muted
          style={{
            width: "100%",
            height: "auto",
            maxHeight: 260,
            display: "block",
          }}
        />
      </div>
      <Text type="secondary" style={{ fontSize: 12, display: "block", marginTop: 6 }}>
        {hint}
      </Text>
    </div>
  );
}
